import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalForm } from 'src/components/form/form/ModalForm';

import {
  useInsertDepartmentTypeGroupByName,
  useInsertDepartmentTypeWithGroupName,
  useInsertDepartmentTypeWithOptionalGroupId,
  useUpdateDepartmentType,
} from '@/hooks/mutations/department';
import {
  useGetDepartmentTypeById,
  useGetDepartmentTypeGroups,
} from '@/hooks/queries';

import DepartmentTypeForm from './forms/DepartmentTypeForm';
import type { DepartmentTypeFormFields } from './forms/departmentTypeSchema';
import {
  defaultValues,
  useDepartmentTypeSchema,
} from './forms/departmentTypeSchema';

type Props = {
  onDismiss: (saved: boolean) => void;
  id?: string;
};

const DepartmentTypeModal: FC<Props> = ({ onDismiss, id }) => {
  const DepartmentTypeSchema = useDepartmentTypeSchema(id);
  const { t } = useTranslation();
  const { insertDepartmentTypeWithGroupName: insert } =
    useInsertDepartmentTypeWithGroupName();
  const { insertDepartmentTypeWithOptionalGroupId: insertWithoutGroup } =
    useInsertDepartmentTypeWithOptionalGroupId();
  const { updateDepartmentType: update } = useUpdateDepartmentType();
  const { insertDepartmentTypeGroupByName: insertDepartmentTypeGroup } =
    useInsertDepartmentTypeGroupByName();

  const { data: departmentTypeResponse, loading: loadingDepartmentType } =
    useGetDepartmentTypeById({
      queryArgs: { id },
      shouldSkip: !id,
    });

  const {
    data: departmentTypeGroupsResponse,
    loading: loadingGroups,
    refetch,
  } = useGetDepartmentTypeGroups({ queryArgs: {} });

  const departmentType = departmentTypeResponse?.department_type[0];
  const departmentTypeValues = departmentTypeResponse?.department_type.map(
    (dt) => ({
      Id: dt.DepartmentTypeId,
      Name: dt.Name ?? undefined,
      Description: dt.Description ?? undefined,
      DepartmentGrpupId: dt.DepartmentTypeGroupId,
      DepartmentGroupName: dt.department_type_group?.Name,
    })
  )[0];

  const departmentGroupOptions =
    departmentTypeGroupsResponse?.department_type_group.map((dtg) => ({
      id: dtg.Id,
      value: dtg.Name,
    })) || [];

  const onSave = async (data: DepartmentTypeFormFields) => {
    if (departmentType) {
      let departmentTypeGroupId: string | undefined;
      if (data.DepartmentGroupName) {
        const existingDepartmentGroup = departmentGroupOptions.find(
          (x) => x.value === data.DepartmentGroupName
        );
        if (!existingDepartmentGroup) {
          const groupInsertResult = await insertDepartmentTypeGroup({
            Name: data.DepartmentGroupName,
          });
          departmentTypeGroupId =
            groupInsertResult.insert_department_type_group_one?.Id;
        } else {
          departmentTypeGroupId = existingDepartmentGroup.id;
        }
      }
      const updateResult = await update({
        OriginalTimestamp: departmentType.ModifiedAtTimestamp,
        DepartmentTypeId: departmentType.DepartmentTypeId,
        Name: data.Name,
        Description: data.Description,
        DepartmentTypeGroupId: departmentTypeGroupId,
      });
      if (updateResult.update_department_type?.affected_rows !== 1) {
        throw new Error(
          'Records not updated. Record may have been updated by another user'
        );
      }
    } else {
      if (data.DepartmentGroupName) {
        const insertResult = await insert({
          Name: data.Name,
          Description: data.Description,
          DepartmentGroupName: data.DepartmentGroupName,
        });
        const departmentTypeId =
          insertResult.insert_department_type_one?.DepartmentTypeId;
        if (!departmentTypeId) {
          throw new Error('Id not found');
        }
      } else {
        const insertWithoutGroupResult = await insertWithoutGroup({
          Name: data.Name,
          Description: data.Description,
          DepartmentTypeGroupId: data.DepartmentGroupId,
        });
        const departmentTypeId =
          insertWithoutGroupResult.insert_department_type_one?.DepartmentTypeId;
        if (!departmentTypeId) {
          throw new Error('Id not found');
        }
      }
    }
    refetch();
  };

  if (loadingDepartmentType || loadingGroups) {
    return null;
  }

  const formId = 'department-type-form';

  return (
    <ModalForm
      values={departmentTypeValues}
      defaultValues={defaultValues}
      schema={DepartmentTypeSchema}
      i18n={t('departments')}
      onSave={onSave}
      onDismiss={onDismiss}
      formId={formId}
      visible={true}
    >
      <DepartmentTypeForm departmentGroupOptions={departmentGroupOptions} />
    </ModalForm>
  );
};

export default DepartmentTypeModal;
